import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Image, Input, message, Popconfirm, Select, Space, Table, Tag, Typography } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { avatarReviewAPI } from '../services/api';

const { Text } = Typography;

const EMPTY_TEXT = '未获取';

function formatDateTime(value) {
  if (value === undefined || value === null || value === '') {
    return EMPTY_TEXT;
  }

  const numericValue = Number(value);
  const date = Number.isFinite(numericValue) && numericValue > 0
    ? new Date(numericValue * 1000)
    : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return EMPTY_TEXT;
  }

  return date.toLocaleString('zh-CN');
}

function renderAvatar(url, alt) {
  if (!url) {
    return <Text type="secondary">无图像</Text>;
  }

  return (
    <Image
      width={88}
      height={88}
      src={url}
      alt={alt}
      style={{ objectFit: 'cover', borderRadius: 8, border: '1px solid #f0f0f0' }}
      fallback="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs="
    />
  );
}

function AvatarReviewManagement() {
  const initialPageSize = 20;
  const [loading, setLoading] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [keyword, setKeyword] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: initialPageSize, total: 0 });
  const [actionLoadingMap, setActionLoadingMap] = useState({});
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [batchActionLoading, setBatchActionLoading] = useState('');
  const [serverScope, setServerScope] = useState('all');
  const currentPage = pagination.current;
  const pageSize = pagination.pageSize;

  const loadReviews = useCallback(async ({
    page = 1,
    pageSize = initialPageSize,
    searchKeyword = ''
  } = {}) => {
    setLoading(true);
    try {
      const response = await avatarReviewAPI.getList({
        page,
        limit: pageSize,
        keyword: searchKeyword,
        server_id: serverScope === 'all' ? undefined : serverScope
      });
      const data = response.data?.data || {};
      const reviewList = data.reviews || [];
      setReviews(reviewList);
      setSelectedRowKeys((prev) => prev.filter((key) => reviewList.some((item) => item.icon_id === key)));
      setPagination((prev) => ({
        ...prev,
        current: page,
        pageSize,
        total: data.pagination?.total || 0
      }));
    } catch (error) {
      message.error('加载待审头像列表失败');
    } finally {
      setLoading(false);
    }
  }, [serverScope]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  const setRowLoading = (iconId, loadingState) => {
    setActionLoadingMap((prev) => ({
      ...prev,
      [iconId]: loadingState
    }));
  };

  const refreshCurrentPage = async () => {
    await loadReviews({
      page: currentPage,
      pageSize,
      searchKeyword: keyword
    });
  };

  const handleApprove = async (record) => {
    setRowLoading(record.icon_id, true);
    try {
      await avatarReviewAPI.approve(record.icon_id, serverScope === 'all' ? {} : { server_id: Number(serverScope) });
      message.success(`头像 ${record.icon_id} 已审核通过`);
      await refreshCurrentPage();
    } catch (error) {
      message.error(error.response?.data?.message || '审核通过失败');
    } finally {
      setRowLoading(record.icon_id, false);
    }
  };

  const handleReject = async (record) => {
    setRowLoading(record.icon_id, true);
    try {
      await avatarReviewAPI.reject(record.icon_id, serverScope === 'all' ? {} : { server_id: Number(serverScope) });
      message.success(`头像 ${record.icon_id} 已拒绝`);
      await refreshCurrentPage();
    } catch (error) {
      message.error(error.response?.data?.message || '审核拒绝失败');
    } finally {
      setRowLoading(record.icon_id, false);
    }
  };

  const pendingCount = useMemo(() => reviews.length, [reviews.length]);
  const selectedReviews = useMemo(
    () => reviews.filter((item) => selectedRowKeys.includes(item.icon_id)),
    [reviews, selectedRowKeys]
  );

  const handleSelectAll = () => {
    if (selectedRowKeys.length === reviews.length) {
      setSelectedRowKeys([]);
      return;
    }
    setSelectedRowKeys(reviews.map((item) => item.icon_id));
  };

  const handleBatchReview = async (action) => {
    if (selectedReviews.length === 0) {
      message.warning('请先选择待审核头像');
      return;
    }

    setBatchActionLoading(action);
    let successCount = 0;
    let failedCount = 0;

    for (const record of selectedReviews) {
      try {
        if (action === 'approve') {
          await avatarReviewAPI.approve(record.icon_id, serverScope === 'all' ? {} : { server_id: Number(serverScope) });
        } else {
          await avatarReviewAPI.reject(record.icon_id, serverScope === 'all' ? {} : { server_id: Number(serverScope) });
        }
        successCount += 1;
      } catch (error) {
        failedCount += 1;
      }
    }

    setBatchActionLoading('');
    setSelectedRowKeys([]);
    await refreshCurrentPage();

    if (failedCount === 0) {
      message.success(`已${action === 'approve' ? '通过' : '拒绝'} ${successCount} 条头像`);
      return;
    }

    message.warning(`已成功处理 ${successCount} 条，失败 ${failedCount} 条`);
  };

  const runSearch = (value = searchDraft) => {
    const nextKeyword = (value || '').trim();
    setSearchDraft(value || '');
    setKeyword(nextKeyword);
    loadReviews({
      page: 1,
      pageSize,
      searchKeyword: nextKeyword
    });
  };

  const columns = [
    {
      title: '头像 ID',
      dataIndex: 'icon_id',
      key: 'icon_id',
      width: 110,
      render: (value) => <Text strong>{value}</Text>
    },
    {
      title: '玩家信息',
      key: 'player',
      width: 220,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Text strong>{record.player_name || EMPTY_TEXT}</Text>
          <Text type="secondary">玩家ID：{record.user_id || EMPTY_TEXT}</Text>
          <Text type="secondary">账号：{record.account_name || EMPTY_TEXT}</Text>
        </Space>
      )
    },
    {
      title: '区服',
      dataIndex: 'server_id',
      key: 'server_id',
      width: 90,
      render: (value) => <Tag color="processing">S{value || 0}</Tag>
    },
    {
      title: '待审头像',
      dataIndex: 'image_url',
      key: 'image_url',
      width: 120,
      render: (value, record) => renderAvatar(value, `review-avatar-${record.icon_id}`)
    },
    {
      title: '当前头像',
      dataIndex: 'current_icon_url',
      key: 'current_icon_url',
      width: 120,
      render: (value, record) => renderAvatar(value, `current-avatar-${record.icon_id}`)
    },
    {
      title: '上传时间',
      dataIndex: 'upload_time',
      key: 'upload_time',
      width: 180,
      render: (value) => formatDateTime(value)
    },
    {
      title: '审核状态',
      key: 'status',
      width: 110,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Tag color="gold">待审核</Tag>
          <Text type="secondary">{record.need_approve ? '需审核' : '状态未同步'}</Text>
        </Space>
      )
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 180,
      render: (_, record) => {
        const rowLoading = Boolean(actionLoadingMap[record.icon_id]) || Boolean(batchActionLoading);
        return (
          <Space>
            <Popconfirm
              title={`确认通过头像 ${record.icon_id} 吗？`}
              onConfirm={() => handleApprove(record)}
              okText="通过"
              cancelText="取消"
            >
              <Button
                type="primary"
                size="small"
                icon={<CheckCircleOutlined />}
                loading={rowLoading}
              >
                通过
              </Button>
            </Popconfirm>
            <Popconfirm
              title={`确认拒绝头像 ${record.icon_id} 吗？`}
              onConfirm={() => handleReject(record)}
              okText="拒绝"
              cancelText="取消"
            >
              <Button
                danger
                size="small"
                icon={<CloseCircleOutlined />}
                loading={rowLoading}
              >
                拒绝
              </Button>
            </Popconfirm>
          </Space>
        );
      }
    }
  ];

  return (
    <div className="page-shell">
      <Card bordered={false} className="detail-card">
        <div className="avatar-review-workspace">
          <div className="avatar-review-overview">
            <div>
              <div className="page-title">头像审核</div>
              <div className="avatar-review-subtitle">集中处理待审头像，快速完成预览、通过和拒绝。</div>
            </div>
            <div className="avatar-review-stat-grid">
              <div className="avatar-review-stat-card">
                <span className="avatar-review-stat-label">待审核总数</span>
                <strong className="avatar-review-stat-value">{pagination.total || 0}</strong>
              </div>
              <div className="avatar-review-stat-card">
                <span className="avatar-review-stat-label">当前页</span>
                <strong className="avatar-review-stat-value">{pendingCount}</strong>
              </div>
              <div className="avatar-review-stat-card">
                <span className="avatar-review-stat-label">已选择</span>
                <strong className="avatar-review-stat-value">{selectedRowKeys.length}</strong>
              </div>
            </div>
          </div>

          <div className="avatar-review-command-bar">
            <div className="avatar-review-command-main">
              <div className="avatar-review-command-input">
                <span className="avatar-review-command-icon">
                  <SearchOutlined />
                </span>
                <Input
                  value={searchDraft}
                  allowClear
                  bordered={false}
                  onChange={(e) => setSearchDraft(e.target.value)}
                  onPressEnter={() => runSearch()}
                  placeholder="搜索头像ID、玩家ID、昵称或账号"
                  className="avatar-review-command-field"
                />
              </div>
              <Button
                type="primary"
                size="large"
                icon={<SearchOutlined />}
                onClick={() => runSearch()}
                className="avatar-review-command-search"
              >
                查询
              </Button>
            </div>
            <div className="avatar-review-command-actions">
              <Select
                size="large"
                value={serverScope}
                style={{ width: 190 }}
                onChange={(value) => {
                  setServerScope(value);
                  setSelectedRowKeys([]);
                }}
                options={[
                  { value: 'all', label: '全区（自动包含新区）' },
                  { value: '1', label: '一区' },
                  { value: '2', label: '二区' }
                ]}
              />
              <Button
                size="large"
                onClick={handleSelectAll}
                disabled={reviews.length === 0 || Boolean(batchActionLoading)}
              >
                {selectedRowKeys.length === reviews.length && reviews.length > 0 ? '取消全选' : '全部选择'}
              </Button>
              <Popconfirm
                title={`确认通过已选择的 ${selectedRowKeys.length} 条头像吗？`}
                onConfirm={() => handleBatchReview('approve')}
                okText="全部通过"
                cancelText="取消"
                disabled={selectedRowKeys.length === 0}
              >
                <Button
                  type="primary"
                  size="large"
                  icon={<CheckCircleOutlined />}
                  loading={batchActionLoading === 'approve'}
                  disabled={selectedRowKeys.length === 0 || batchActionLoading === 'reject'}
                >
                  全部通过
                </Button>
              </Popconfirm>
              <Popconfirm
                title={`确认拒绝已选择的 ${selectedRowKeys.length} 条头像吗？`}
                onConfirm={() => handleBatchReview('reject')}
                okText="全部拒绝"
                cancelText="取消"
                disabled={selectedRowKeys.length === 0}
              >
                <Button
                  danger
                  size="large"
                  icon={<CloseCircleOutlined />}
                  loading={batchActionLoading === 'reject'}
                  disabled={selectedRowKeys.length === 0 || batchActionLoading === 'approve'}
                >
                  全部拒绝
                </Button>
              </Popconfirm>
              <Button
                icon={<ReloadOutlined />}
                size="large"
                onClick={refreshCurrentPage}
                disabled={Boolean(batchActionLoading)}
              >
                刷新
              </Button>
            </div>
          </div>

          <div className="avatar-review-summary">
            <Tag color="gold">待审核 {pagination.total || 0} 条</Tag>
            <Tag color="blue">当前页 {pendingCount} 条</Tag>
            {selectedRowKeys.length > 0 ? <Tag color="green">已选择 {selectedRowKeys.length} 条</Tag> : null}
          </div>
        </div>
      </Card>

      <Card title="未审核列表" bordered={false}>
        <Table
          rowKey="icon_id"
          loading={loading}
          columns={columns}
          dataSource={reviews}
          locale={{ emptyText: '当前没有待审核头像' }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`
          }}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys)
          }}
          onChange={(pag) => loadReviews({
            page: pag.current,
            pageSize: pag.pageSize,
            searchKeyword: keyword
          })}
          scroll={{ x: 1200 }}
        />
      </Card>
    </div>
  );
}

export default AvatarReviewManagement;
